// Het overzicht van een notitie: de koppen op een rij, met een klik ernaartoe.
//
// Bij een lange notitie — een handleiding, een logboek van een klant — is scrollen naar "die
// ene kop" het werk dat je het vaakst doet. De koppen komen uit de editor zelf en niet uit de
// opgeslagen tekst, zodat een kop die je net typt er meteen bij staat.

import { useEffect, useState } from "react";
import type { Editor as TipTapEditor } from "@tiptap/react";
import { Icoon } from "./Icoon.tsx";

export type Kop = { plek: number; niveau: number; tekst: string };

export function kopppenUit(editor: TipTapEditor | null): Kop[] {
  if (!editor) return [];
  const koppen: Kop[] = [];
  editor.state.doc.descendants((knoop, plek) => {
    if (knoop.type.name === "heading") {
      koppen.push({ plek, niveau: Number(knoop.attrs.level ?? 1), tekst: knoop.textContent || "Zonder kop" });
    }
    return true;
  });
  return koppen;
}

type Props = {
  editor: TipTapEditor | null;
  sluit: () => void;
};

export function Overzicht({ editor, sluit }: Props) {
  const [koppen, setKoppen] = useState<Kop[]>(() => kopppenUit(editor));

  // Blijf meelopen met wat er getypt wordt: een kop die erbij komt hoort er meteen bij te staan.
  useEffect(() => {
    if (!editor) return;
    const bijwerken = () => setKoppen(kopppenUit(editor));
    editor.on("update", bijwerken);
    return () => {
      editor.off("update", bijwerken);
    };
  }, [editor]);

  const springNaar = (kop: Kop) => {
    if (!editor) return;
    editor.chain().focus().setTextSelection(kop.plek + 1).run();
    const knoop = editor.view.domAtPos(kop.plek + 1).node;
    const element = knoop instanceof HTMLElement ? knoop : knoop.parentElement;
    element?.scrollIntoView({ block: "start", behavior: "smooth" });
    sluit();
  };

  return (
    <div className="overlay" onClick={sluit}>
      <div className="paneel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Overzicht van deze notitie">
        <div className="paneelkop">
          <Icoon naam="lijstje" grootte={19} />
          <strong style={{ flexGrow: 1 }}>Overzicht</strong>
          <button className="knop stil" onClick={sluit} aria-label="Sluiten">
            <Icoon naam="kruis" grootte={15} />
          </button>
        </div>

        <div className="paneelinhoud">
          {koppen.length === 0 && (
            <p className="leeg">
              Deze notitie heeft nog geen koppen. Maak er een met <strong>Aa</strong> in de werkbalk, of typ{" "}
              <code>## </code> aan het begin van een regel.
            </p>
          )}
          {koppen.map((kop) => (
            <button
              key={`${kop.plek}-${kop.tekst}`}
              className="overzichtregel"
              style={{ paddingLeft: 12 + (Math.min(kop.niveau, 4) - 1) * 16 }}
              onClick={() => springNaar(kop)}
            >
              {kop.tekst}
            </button>
          ))}
        </div>

        <div className="paneelvoet">
          <span>{koppen.length === 1 ? "1 kop" : `${koppen.length} koppen`}</span>
        </div>
      </div>
    </div>
  );
}
